import React, { useEffect, useRef, useState } from "react";

// TailwindCSS is required for styling

const Modal = ({
  isOpen,
  onClose,
  mediaUrl,
  darkMode = false /* pass true for dark mode */
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [drag, setDrag] = useState({ active: false, x: 0, y: 0, originX: 0, originY: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef();
  const modalRef = useRef();

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen) {
      const focusable = modalRef.current.querySelectorAll(
        'button, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      function trap(e) {
        if (e.key === "Tab") {
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      }
      modalRef.current.addEventListener("keydown", trap);
      first && first.focus();
      return () => modalRef.current && modalRef.current.removeEventListener("keydown", trap);
    }
  }, [isOpen]);

  // ESC to close & click outside to close
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    function onClick(e) {
      if (modalRef.current && e.target === modalRef.current) onClose();
    }
    window.addEventListener("keydown", onKey);
    modalRef.current && modalRef.current.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      modalRef.current && modalRef.current.removeEventListener("mousedown", onClick);
    };
  }, [isOpen, onClose]);

  // Reset zoom/position on open/close
  useEffect(() => {
    setZoomLevel(1);
    setDrag({ active: false, x: 0, y: 0, originX: 0, originY: 0 });
    setImgLoaded(false);
  }, [isOpen, mediaUrl]);

  // Drag to pan
  const handleMouseDown = (e) => {
    if (zoomLevel === 1) return;
    setDrag({
      active: true,
      originX: e.clientX - drag.x,
      originY: e.clientY - drag.y,
      x: drag.x,
      y: drag.y
    });
    e.preventDefault();
  };
  const handleMouseMove = (e) => {
    if (!drag.active) return;
    setDrag((prev) => ({
      ...prev,
      x: e.clientX - prev.originX,
      y: e.clientY - prev.originY
    }));
  };
  const handleMouseUp = () => setDrag((prev) => ({ ...prev, active: false }));

  // Touch events for mobile drag
  const handleTouchStart = (e) => {
    if (zoomLevel === 1) return;
    setDrag({
      active: true,
      originX: e.touches[0].clientX - drag.x,
      originY: e.touches[0].clientY - drag.y,
      x: drag.x,
      y: drag.y
    });
  };
  const handleTouchMove = (e) => {
    if (!drag.active) return;
    setDrag((prev) => ({
      ...prev,
      x: e.touches[0].clientX - prev.originX,
      y: e.touches[0].clientY - prev.originY
    }));
  };
  const handleTouchEnd = () => setDrag((prev) => ({ ...prev, active: false }));

  // Download
  const handleDownload = () => {
    try {
      const link = document.createElement("a");
      link.href = mediaUrl;
      link.download = mediaUrl.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {}
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev * 1.2, 4));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev / 1.2, 1));
  const handleReset = () => {
    setZoomLevel(1);
    setDrag({ active: false, x: 0, y: 0, originX: 0, originY: 0 });
  };

  if (!isOpen) return null;

  return (
    <div
      className={`z-50 fixed inset-0 flex items-center justify-center select-none
        ${darkMode ? "dark" : ""}
      `}
      aria-modal="true"
      role="dialog"
      ref={modalRef}
      tabIndex={-1}
      style={{
        // Slight animated gradient overlay
        background: darkMode
          ? "linear-gradient(120deg, #232526cc 0%, #414345cc 100%)"
          : "linear-gradient(120deg, #e0eafcbb 0%, #cfdef3bb 100%)"
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Glass/Neumorphic Modal */}
      <div
        className={`
          relative max-w-[90vw] max-h-[90vh] w-full md:w-[600px] 
          rounded-[2rem] shadow-2xl p-0
          bg-white/60 dark:bg-gray-900/80
          border border-gray-200 dark:border-gray-800
          transition-all duration-300
          backdrop-blur-[9px]
          flex flex-col
          animate-[modalAppear_0.3s_ease]
        `}
        style={{
          boxShadow: darkMode
            ? "0 6px 40px 0 #1118, 0 2px 8px #1115"
            : "0 6px 44px 0 #b6c0d6aa, 0 2px 8px #dbe6ff88",
          border: "1.5px solid rgba(200,200,210,0.17)",
        }}
        onMouseDown={e => e.stopPropagation()} // Prevent click-outside close
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className={`
            absolute top-4 right-4 z-20
            p-3 bg-white/50 dark:bg-gray-800/60 rounded-full shadow
            hover:bg-red-100 dark:hover:bg-red-900
            transition-all
            text-gray-700 dark:text-gray-200
            focus:outline-none focus:ring-2 focus:ring-blue-400
          `}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Loader spinner */}
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="w-14 h-14 border-4 border-blue-400 dark:border-blue-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Image Container */}
        <div
          className={`
            flex-1 flex items-center justify-center min-h-[300px] relative
            overflow-hidden rounded-[2rem] m-0
            group
            ${imgLoaded ? "bg-transparent" : "bg-gray-200/40 dark:bg-gray-800/30"}
            transition-colors
          `}
          style={{
            touchAction: zoomLevel > 1 ? "pan-x pan-y" : "auto"
          }}
        >
          <img
            ref={imgRef}
            src={mediaUrl}
            alt="Attachment"
            draggable={false}
            style={{
              transform: `scale(${zoomLevel}) translate(${drag.x / zoomLevel}px, ${drag.y / zoomLevel}px)`,
              transition: drag.active
                ? "none"
                : "transform 0.35s cubic-bezier(.4,2,.3,1), box-shadow 0.23s",
              cursor: zoomLevel > 1 ? (drag.active ? "grabbing" : "grab") : "zoom-in",
              boxShadow: zoomLevel > 1
                ? "0 8px 44px -8px #0687ff55"
                : "0 4px 24px -10px #aebbff44"
            }}
            className={`
              w-auto max-w-full max-h-[70vh] mx-auto rounded-[1.2rem]
              select-none
              shadow-lg
              transition-shadow
              ${imgLoaded ? "opacity-100" : "opacity-0"}
              duration-500
            `}
            onLoad={() => setImgLoaded(true)}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            tabIndex={0}
            aria-label="Preview image"
          />
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-8 pb-6 pt-2 gap-2">
          {/* Zoom Controls */}
          <div className="inline-flex gap-2">
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel <= 1}
              className={`
                p-2 rounded-full bg-gray-100 dark:bg-gray-800 shadow
                hover:bg-blue-100 dark:hover:bg-blue-900
                focus:outline-none focus:ring-2 focus:ring-blue-400
                text-lg text-blue-600 dark:text-blue-300
                transition-all disabled:opacity-60
              `}
              aria-label="Zoom out"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                <path strokeLinecap="round" strokeWidth="2" d="M5 10h10" />
              </svg>
            </button>
            <button
              onClick={handleReset}
              disabled={zoomLevel === 1}
              className={`
                p-2 rounded-full bg-gray-100 dark:bg-gray-800 shadow
                hover:bg-green-100 dark:hover:bg-green-900
                focus:outline-none focus:ring-2 focus:ring-green-400
                text-lg text-green-600 dark:text-green-300
                transition-all disabled:opacity-60
              `}
              aria-label="Reset zoom"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="7" strokeWidth="2" />
                <path strokeLinecap="round" strokeWidth="2" d="M10 5v5l3 2" />
              </svg>
            </button>
            <button
              onClick={handleZoomIn}
              disabled={zoomLevel >= 4}
              className={`
                p-2 rounded-full bg-gray-100 dark:bg-gray-800 shadow
                hover:bg-blue-100 dark:hover:bg-blue-900
                focus:outline-none focus:ring-2 focus:ring-blue-400
                text-lg text-blue-600 dark:text-blue-300
                transition-all disabled:opacity-60
              `}
              aria-label="Zoom in"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                <path strokeLinecap="round" strokeWidth="2" d="M10 5v10M5 10h10" />
              </svg>
            </button>
          </div>
          {/* Zoom Level Indicator */}
          <span
            className="text-sm text-gray-600 dark:text-gray-300 px-3 select-none"
            aria-live="polite"
          >
            {zoomLevel.toFixed(2)}&times;
          </span>
          {/* Download Button */}
          <button
            onClick={handleDownload}
            className={`
              px-4 py-2 rounded-full bg-gradient-to-tr from-blue-500 to-cyan-500
              text-white font-semibold shadow-lg
              hover:from-blue-600 hover:to-cyan-600
              focus:outline-none focus:ring-2 focus:ring-blue-200
              flex items-center gap-2 transition-all
            `}
            aria-label="Download image"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeWidth="2" d="M7 17l5 5 5-5M12 12v10" />
              <path strokeLinecap="round" strokeWidth="2" d="M12 3v9" />
            </svg>
            Download
          </button>
        </div>
      </div>
      {/* Subtle noisy overlay for depth */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[-1]"
        style={{
          backgroundImage:
            "url('https://www.transparenttextures.com/patterns/noise.png')",
          opacity: darkMode ? 0.13 : 0.08,
          mixBlendMode: "soft-light"
        }}
      />
      <style>{`
        @keyframes modalAppear {
          from { transform: scale(0.94) translateY(30px); opacity: 0.1; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Modal;